import { ArrowCounterClockwise } from '@phosphor-icons/react';
import React, { useMemo } from 'react';
import { Button } from '../../../Utility/Button';
import { ButtonVariant } from '../../../../Types/Enums';
import { Prompt } from '../../../../Types';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../Store/Store';

interface ResetConfig {
    title: string;
    content: string;
    resetFunction: () => void;
    noModal?: boolean;
}

interface ResetButtonProps {
    configKey: string;
    prompt: Prompt;
    promptSnapshot: Prompt;
    PromptActions: any;
    stopPropagation?: boolean;
    customButton?: {
        variant: ButtonVariant;
        content: React.ReactNode;
    };
}

type ResetConfigsType = {
    [key: string]: () => ResetConfig;
    basicInfo: () => ResetConfig;
    systemPrompt: () => ResetConfig;
    connector: () => ResetConfig;
    functions: () => ResetConfig;
    functionsExtendet: () => ResetConfig;
    description: () => ResetConfig;
    knowledgebase: () => ResetConfig;
    dataProtection: () => ResetConfig;
    groups: () => ResetConfig;
};

const useResetConfigs = (
    prompt: Prompt,
    promptSnapshot: Prompt,
    updatePrompt: (updates: Partial<Prompt>) => void,
): ResetConfigsType => {
    const i18n = useSelector((state: RootState) => state.i18n.i18n);

    return useMemo(
        () => ({
            basicInfo: (): ResetConfig => ({
                title: i18n.translate({ id: 'resetbutton.name.title', sourceName: 'Prompt' }),
                content: i18n.translate({ id: 'resetbutton.name.content', sourceName: 'Prompt' }),
                resetFunction: () => updatePrompt({ name: promptSnapshot.name }),
            }),
            systemPrompt: (): ResetConfig => ({
                title: i18n.translate({ id: 'resetbutton.systemPrompt.title', sourceName: 'Prompt' }),
                content: i18n.translate({ id: 'resetbutton.systemPrompt.content', sourceName: 'Prompt' }),
                resetFunction: () => updatePrompt({ value: promptSnapshot.value }),
                noModal: true,
            }),
            model: (): ResetConfig => ({
                title: i18n.translate({ id: 'resetbutton.model.title', sourceName: 'Prompt' }),
                content: i18n.translate({ id: 'resetbutton.model.content', sourceName: 'Prompt' }),
                resetFunction: () => updatePrompt({ model: promptSnapshot.model }),
            }),
            modelExtendet: (): ResetConfig => ({
                title: i18n.translate({ id: 'resetbutton.model.extendet.title', sourceName: 'Prompt' }),
                content: i18n.translate({ id: 'resetbutton.model.extendet.content', sourceName: 'Prompt' }),
                resetFunction: () =>
                    updatePrompt({
                        model: promptSnapshot.model,
                        functions: [
                            ...promptSnapshot.functions.filter((f) => f !== 'code_interpreter' && f !== 'file_search'),
                        ],
                        files: promptSnapshot.files,
                    }),
            }),
            connector: (): ResetConfig => ({
                title: i18n.translate({ id: 'resetbutton.connector.title', sourceName: 'Prompt' }),
                content: i18n.translate({ id: 'resetbutton.connector.content', sourceName: 'Prompt' }),
                resetFunction: () =>
                    updatePrompt({
                        functions: [
                            ...promptSnapshot.functions.filter((f) => f !== 'code_interpreter' && f !== 'file_search'),
                            ...prompt.functions.filter((f) => f === 'code_interpreter' || f === 'file_search'),
                        ],
                    }),
            }),
            functions: (): ResetConfig => ({
                title: i18n.translate({ id: 'resetbutton.functions.title', sourceName: 'Prompt' }),
                content: i18n.translate({ id: 'resetbutton.functions.content', sourceName: 'Prompt' }),
                resetFunction: () =>
                    updatePrompt({
                        functions: [
                            ...promptSnapshot.functions.filter((f) => f === 'code_interpreter' || f === 'file_search'),
                            ...prompt.functions.filter((f) => f !== 'code_interpreter' && f !== 'file_search'),
                        ],
                        files: promptSnapshot.files,
                    }),
            }),
            functionsExtendet: (): ResetConfig => ({
                title: i18n.translate({ id: 'resetbutton.functions.extendet.title', sourceName: 'Prompt' }),
                content: i18n.translate({ id: 'resetbutton.functions.extendet.content', sourceName: 'Prompt' }),
                resetFunction: () =>
                    updatePrompt({
                        functions: [
                            ...promptSnapshot.functions.filter((f) => f === 'code_interpreter' || f === 'file_search'),
                            ...prompt.functions.filter((f) => f !== 'code_interpreter' && f !== 'file_search'),
                        ],
                        model: promptSnapshot.model,
                        files: promptSnapshot.files,
                    }),
            }),
            description: (): ResetConfig => ({
                title: i18n.translate({ id: 'resetbutton.description.title', sourceName: 'Prompt' }),
                content: i18n.translate({ id: 'resetbutton.description.content', sourceName: 'Prompt' }),
                resetFunction: () =>
                    updatePrompt({
                        description: promptSnapshot.description,
                        examples: promptSnapshot.examples,
                    }),
            }),
            knowledgebase: (): ResetConfig => ({
                title: i18n.translate({ id: 'resetbutton.knowledgebase.title', sourceName: 'Prompt' }),
                content: i18n.translate({ id: 'resetbutton.knowledgebase.content', sourceName: 'Prompt' }),
                resetFunction: () => updatePrompt({ knowledgebases: promptSnapshot.knowledgebases }),
            }),
            dataProtection: (): ResetConfig => ({
                title: i18n.translate({ id: 'resetbutton.dataProtection.title', sourceName: 'Prompt' }),
                content: i18n.translate({ id: 'resetbutton.dataProtection.content', sourceName: 'Prompt' }),
                resetFunction: () => updatePrompt({ dlp: promptSnapshot.dlp }),
            }),
            groups: (): ResetConfig => ({
                title: i18n.translate({ id: 'resetbutton.groups.title', sourceName: 'Prompt' }),
                content: i18n.translate({ id: 'resetbutton.groups.content', sourceName: 'Prompt' }),
                resetFunction: () => updatePrompt({ groups: promptSnapshot.groups }),
            }),
        }),
        [i18n, prompt, promptSnapshot, updatePrompt],
    );
};

const ResetButton = ({
    configKey,
    prompt,
    promptSnapshot,
    PromptActions,
    stopPropagation = false,
    customButton,
}: ResetButtonProps) => {
    const resetConfigs = useResetConfigs(prompt, promptSnapshot, PromptActions.updatePrompt);
    const i18n = useSelector((state: RootState) => state.i18n.i18n);

    const handleClick = (e: React.MouseEvent) => {
        if (stopPropagation) e.stopPropagation();

        let config: ResetConfig;
        // Special case for functions if model compatibility needs to be checked
        if (configKey === 'functions' && !PromptActions.checkAssistantModel(prompt.model)) {
            config = resetConfigs.functionsExtendet();
        } else if (configKey === 'model' && !PromptActions.checkAssistantModel(promptSnapshot.model)) {
            console.log('model-extendet');
            config = resetConfigs.modelExtendet();
        } else if (resetConfigs[configKey]) {
            config = resetConfigs[configKey]();
        } else {
            console.error(`Reset config for key "${configKey}" not found`);
            return;
        }

        PromptActions.handleResetField(config);
    };

    return (
        <Button
            variant={customButton?.variant || 'transparentRound'}
            onClick={handleClick}
            tooltipText={i18n.translate({ id: 'resetbutton.tooltip', sourceName: 'Prompt' })}
        >
            {customButton?.content || <ArrowCounterClockwise size={18} />}
        </Button>
    );
};

export default ResetButton;
